import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, take, tap } from 'rxjs/operators';
import { AddonGroup, AddonNode, Category, Facet, SoldTo, SolrSearchResult } from '../../../../core/model';
import { PrincipalConfigurationService, UserFacade } from '../../../../core/user';
import { deepEqualArrays, prepareUrlForLink } from '../../../../core/util';
import { DrawerService, DrawerWidth } from '../../../../shared';
import { SoldToFacade } from '../../../sold-to-base';
import { CatalogSearchService, CatalogTabTypes } from '../../container/catalog/catalog-search.service';

@Component({
  selector: 'py-catalog-filter',
  templateUrl: './catalog-filter.component.html',
  styleUrls: ['./catalog-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CatalogFilterComponent implements OnInit, OnDestroy {
  @ViewChild('drawerTemplate') drawerTemplate: TemplateRef<any>;
  showAllCategories = false;
  hasTendered$: Observable<boolean>;
  hasCustomerAssortment$: Observable<boolean>;
  tenderedArticles$: Observable<boolean>;
  myAssortment$: Observable<boolean>;
  outlet$: Observable<boolean>;
  lastChance$: Observable<boolean>;
  myaParam$: Observable<string>;
  activeSoldTo$: Observable<SoldTo>;
  facets$: Observable<Facet[]>;
  customFilterCount$: Observable<number>;
  searchResults$ = new BehaviorSubject<SolrSearchResult>(undefined);
  private initialExpandedFacets: Facet[] = [];
  private initialShowAllFacets: { [key: string]: boolean } = {};

  @Input() activeTabIndex$: Observable<CatalogTabTypes>;
  @Input() hasAreaOfUse: boolean;
  @Input() categories: Category[];
  @Input() addonGroup: AddonGroup;
  @Input() addonNode: AddonNode;
  @Input() showSort = true;

  @Input() set searchResults(searchResults: SolrSearchResult) {
    this.searchResults$.next(searchResults);
  }

  @Output() handleFilter = new EventEmitter<string>();

  constructor(
    private drawerService: DrawerService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userService: UserFacade,
    private principalConfigurationService: PrincipalConfigurationService,
    private soldToService: SoldToFacade,
    private catalogSearchService: CatalogSearchService
  ) {}

  ngOnDestroy(): void {
    //Close filter drawer when we leave the catalog
    this.drawerService.closeOpenDrawer();
  }

  ngOnInit(): void {
    this.hasTendered$ = combineLatest([
      this.principalConfigurationService.isEnabled('enableTendered'),
      this.userService.hasTendered(),
    ]).pipe(map(([enableTendered, hasTendered]) => Boolean(enableTendered && hasTendered)));

    this.hasCustomerAssortment$ = combineLatest([
      this.principalConfigurationService.isEnabled('enableTogglingOfCustomerAssortment'),
      this.userService.hasCustomerAssortment(),
    ]).pipe(
      map(([enableTogglingOfCustomerAssortment, hasCustomerAssortment]) =>
        Boolean(enableTogglingOfCustomerAssortment && hasCustomerAssortment)
      )
    );

    this.tenderedArticles$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('t') === '1'),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.myAssortment$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('mya') === '1'),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.outlet$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('o') === '1'),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.lastChance$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('zt') === '1'),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.facets$ = this.searchResults$.pipe(
      map((searchResults) => this.catalogSearchService.getFacets(searchResults)),
      distinctUntilChanged(deepEqualArrays),
      tap((facets) => {
        this.initialExpandedFacets = facets?.filter((f) => f?.values[0].name?.toLowerCase() !== 'true').slice(0, 3);
      })
    );

    this.customFilterCount$ = combineLatest([
      this.tenderedArticles$,
      this.myAssortment$,
      this.outlet$,
      this.facets$,
      this.lastChance$,
    ]).pipe(
      map(([tenderedArticles, myAssortment, outlet, facets, lastChance]) => {
        let count = 0;

        if (tenderedArticles) {
          count = count + 1;
        }
        if (myAssortment) {
          count = count + 1;
        }
        if (outlet) {
          count = count + 1;
        }
        if (lastChance) {
          count = count + 1;
        }
        if (facets) {
          const facetsCount = facets.reduce(
            (total, facet) => total + facet.values.reduce((t, currentValue) => t + (currentValue.selected ? 1 : 0), 0),
            0
          );
          count = count + facetsCount;
        }
        return count;
      })
    );

    this.activeSoldTo$ = this.soldToService.getActiveSoldTo().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  openDrawer() {
    this.drawerService
      .open({
        template: this.drawerTemplate,
        width: DrawerWidth.NearFull,
        closeOnNavigation: false,
      })
      .pipe(take(1))
      .subscribe();
  }

  handleChange($event) {
    this.handleFilter.emit($event);
  }

  handleShowAll($event: boolean, facet: Facet) {
    if (facet.code) {
      this.initialShowAllFacets = { ...this.initialShowAllFacets, [facet.code]: $event };
    }
  }

  get getCategories(): Category[] {
    if (this.showAllCategories) {
      return this.categories;
    }

    return this.categories.slice(0, 12);
  }

  updateQueryStringParameter(queryParams: object) {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
      state: {
        preserveScrollPosition: true,
      },
    });
  }

  categoryUrl(category: Category): string {
    const currentQueryParams = this.activatedRoute.snapshot.queryParams;
    let query: string = currentQueryParams.query;

    if (!!currentQueryParams.aou) {
      if (!!query?.match(/articleAreasOfUse:[^:&]+/)) {
        query = query.replace(/articleAreasOfUse:[^:&]+/, `articleAreasOfUse:${category.code}`);
      } else {
        query = query || '::';
        query = `${query}${query.endsWith(':') ? '' : ':'}articleAreasOfUse:${category.code}`;
      }

      const categoryUrl = category.url.replace('{category-name}', category.name);
      return prepareUrlForLink(categoryUrl, { ...currentQueryParams, aou: category.code, query: query });
    }

    if (!!query?.match(/allCategories:[^:&]+/)) {
      query = query.replace(/allCategories:[^:&]+/, `allCategories:${category.code}`);
    } else {
      query = query || '::';
      query = `${query}${query.endsWith(':') ? '' : ':'}allCategories:${category.code}`;
    }

    return prepareUrlForLink(category.url, { ...currentQueryParams, query: query });
  }

  initialCollapsed(facet: Facet): boolean {
    return this.initialExpandedFacets.find((f) => f === facet) ||
      (facet.values[0].name.toLowerCase() !== 'true' && facet.values.filter((v) => v.selected).length > 0)
      ? false
      : true;
  }

  initialShowAll(facet: Facet): boolean {
    return facet.code ? !!this.initialShowAllFacets[facet.code] : false;
  }

  resetFacets(): void {
    // Reset query and set default values for custom filters
    this.catalogSearchService.setQueryParams({
      ...this.activatedRoute.snapshot.queryParams,
      query: undefined,
      mya: '0',
      t: null,
      o: null,
      zt: null,
    });

    this.drawerService.closeOpenDrawer();
  }
}
